import { CSSProperties } from "react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { lookupFaIcon } from "./faRegistry";

/**
 * A Font Awesome icon named the way the old Font Awesome Kit took it: a class string such as
 * "fal fa-chevron-right", optionally with any other classes mixed in
 * (`classNames(item.icon, styles.rowIcon)`). The style prefix and icon name pick the definition
 * from faRegistry; every other class, including FA modifiers (fa-fw, fa-spin, fa-lg, fa-rotate-90,
 * ...), stays on the rendered <svg>, so existing sizing, colour and rotation styles still apply.
 *
 * An icon missing from the registry draws no glyph (an empty span carrying the other classes, so
 * layout holds) and warns once in development.
 */

const STYLE_PREFIX: Record<string, string> = {
    fa: "fas",
    fas: "fas",
    "fa-solid": "fas",
    far: "far",
    "fa-regular": "far",
    fal: "fal",
    "fa-light": "fal",
    fat: "fat",
    "fa-thin": "fat",
    fad: "fad",
    "fa-duotone": "fad",
    fab: "fab",
    "fa-brands": "fab",
    fak: "fak",
    "fa-kit": "fak",
};

// FA utility classes, not icon names: they pass through as classes (fontawesome-svg-core's CSS styles them).
const MODIFIER =
    /^fa-(fw|2xs|xs|sm|lg|xl|2xl|\d+x|spin|spin-pulse|spin-reverse|pulse|beat|fade|beat-fade|bounce|flip|shake|rotate-\d+|rotate-by|flip-horizontal|flip-vertical|flip-both|border|pull-left|pull-right|inverse|li|ul|stack|stack-1x|stack-2x|swap-opacity|sharp|width-auto)$/;

export interface ResolvedFaIcon {
    /** Undefined when the class string names an icon the registry does not hold. */
    definition?: IconDefinition;
    /** The prefix and name the class string asked for, e.g. "fal fa-chevron-right" (for warnings). */
    requested?: string;
    /** Every class that is not the style prefix or icon name. */
    rest: string[];
}

export function resolveFaIcon(classString: string | undefined | null): ResolvedFaIcon {
    const rest: string[] = [];
    let prefix: string | undefined;
    let name: string | undefined;
    for (const token of (classString ?? "").split(/\s+/)) {
        if (!token) continue;
        if (STYLE_PREFIX[token]) {
            prefix = prefix ?? STYLE_PREFIX[token];
        } else if (!name && token.startsWith("fa-") && !MODIFIER.test(token)) {
            name = token.slice(3);
        } else {
            rest.push(token);
        }
    }
    if (!name) return { rest };
    // FA6: a bare "fa-x" with no style class is solid.
    const style = prefix ?? "fas";
    return { definition: lookupFaIcon(style, name), requested: `${style} fa-${name}`, rest };
}

const warned = new Set<string>();
function warnMissing(requested: string | undefined, source: string | undefined | null) {
    if (process.env.NODE_ENV === "production") return;
    const key = requested ?? String(source);
    if (warned.has(key)) return;
    warned.add(key);
    // eslint-disable-next-line no-console
    console.warn(`[FaIcon] No icon for "${source}"${requested ? ` (${requested})` : ""}. Add it to src/components/fa-icon/faRegistry.ts.`);
}

interface FaIconProps {
    /** The kit-style class string: "fal fa-chevron-right" plus any other classes. */
    className?: string;
    style?: CSSProperties;
    title?: string;
    "aria-hidden"?: boolean | "true" | "false";
}

/** The <svg> markup fontawesome-svg-core would produce, drawn directly: the runtime itself (about
 * 40 KB gzipped with react-fontawesome) would otherwise sit in _app, since the menu uses icons.
 * Sizing, spin, rotation and duotone opacity come from fontawesome-svg-core/styles.css (_app). */
function svgParts(definition: IconDefinition) {
    const [width, height, , , path] = definition.icon;
    const viewBox = `0 0 ${width} ${height}`;
    const baseClass = `svg-inline--fa fa-${definition.iconName}`;
    return { viewBox, baseClass, paths: Array.isArray(path) ? path : [path] };
}

export default function FaIcon({ className, style, title, ...rest }: FaIconProps) {
    const resolved = resolveFaIcon(className);
    if (!resolved.definition) {
        if (resolved.requested) warnMissing(resolved.requested, className);
        // No glyph, but keep the box the other classes give it (a menu row's fixed-width icon
        // column), so labels stay aligned as they did when the kit left an unknown icon blank.
        return resolved.rest.length ? <span aria-hidden="true" className={resolved.rest.join(" ")} style={style} /> : null;
    }
    const { viewBox, baseClass, paths } = svgParts(resolved.definition);
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox={viewBox} className={[baseClass, ...resolved.rest].join(" ")} style={style} role="img" focusable="false" aria-hidden={title ? undefined : (rest["aria-hidden"] ?? true)}>
            {title && <title>{title}</title>}
            {paths.length === 2 ? (
                <g className="fa-duotone-group">
                    <path className="fa-secondary" fill="currentColor" d={paths[0]} />
                    <path className="fa-primary" fill="currentColor" d={paths[1]} />
                </g>
            ) : (
                <path fill="currentColor" d={paths[0]} />
            )}
        </svg>
    );
}

/**
 * Stored rich-text HTML (module help) carries kit icons as `<i class="fal fa-newspaper"></i>`.
 * This swaps each one the registry knows for its inline <svg>; unknown ones are left as they are.
 * The svg gets its 1em sizing inline as well as through the global FA stylesheet, so the markup
 * stays the right size if it is shown somewhere without that stylesheet.
 */
export function inlineFaIcons(html: string | undefined | null): string {
    if (!html || html.indexOf("fa-") === -1) return html ?? "";
    return html.replace(/<i([^>]*)>\s*<\/i>/gi, (match, attrs: string) => {
        const cls = /class\s*=\s*"([^"]*)"/i.exec(attrs)?.[1] ?? /class\s*=\s*'([^']*)'/i.exec(attrs)?.[1];
        if (!cls || cls.indexOf("fa") === -1) return match;
        const resolved = resolveFaIcon(cls);
        if (!resolved.definition) {
            warnMissing(resolved.requested, cls);
            return match;
        }
        const { viewBox, baseClass, paths } = svgParts(resolved.definition);
        const classes = [baseClass, ...resolved.rest].join(" ").replace(/"/g, "");
        const body =
            paths.length === 2
                ? `<g class="fa-duotone-group"><path class="fa-secondary" fill="currentColor" d="${paths[0]}"></path><path class="fa-primary" fill="currentColor" d="${paths[1]}"></path></g>`
                : `<path fill="currentColor" d="${paths[0]}"></path>`;
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" class="${classes}" style="height:1em;overflow:visible;vertical-align:-0.125em" aria-hidden="true" focusable="false" role="img">${body}</svg>`;
    });
}
